import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { HTTP } from "../../api/resources";
import { all as translations } from "../../modules/translations/translations";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import SelectOptionsModal from "../Helpers/Modal/SelectOptionsModal";
import {
  sortableContainer,
  sortableElement,
} from "react-sortable-hoc";
import arrayMove from "array-move";
import DragHandle from "../Helpers/Utilities/DragAndDrop/DragHandle";
import TextFormatWarning from "./Modals/TextFormatWarning";

// Sortable items, do not relocate
const SortableItem = sortableElement(({
  field, rightFieldForAction, deleting, handleSetDeleting, props, openModal,
  updateField, handleOpenModal, closeOptionsModal, handleDelete, changesRequireWarning,
  getFieldId, naming
}) => (
  <div>
    {field.id &&
      <DragHandle />
    }
    <div className="row">
      <div className="col-md-3">
        {field.system_owned && (
          <div className='align-items-center d-flex h-100'>
            <span className='font-weight-bold field-name-mobile'>
              {field.name}
            </span>
          </div>
        )}
        {!field.system_owned && (
          <div className='form-group'>
            <label>Field Label</label>
            <input
              className='form-control'
              value={field.name}
              onChange={(event) => {
                updateField(field, { name: event.target.value });
              }}
            />
          </div>
        )}
      </div>

      {rightFieldForAction(deleting, field) && (
        <React.Fragment>
          <div className='col-md-5'>
            Please confirm. This will remove the field data from all{" "}
            {translations[props.recordType]} records.
            <strong> This can't be undone.</strong>
          </div>
          <div className='col-md-4'>
            <button
              onClick={() => {
                handleSetDeleting(false);
              }}
              type='button'
              className='btn btn-secondary mr-2'
            >
              Cancel
            </button>

            <button
              onClick={() => {
                handleDelete(field);
              }}
              type='button'
              className='btn btn-outline-danger'
            >
              Yes, delete {field.name}
            </button>
          </div>
        </React.Fragment>
      )}

    {!rightFieldForAction(deleting, field) && (
      <React.Fragment>
        <div className='col-md-4'>
          <div className='form-group'>
            <div>
              <label>Field Type</label>
              {field.system_owned && <p>{field.friendly_name}</p>}
            </div>
            {!field.system_owned && (
              <select
                value={field.value_type}
                onChange={(event) => {
                  if (changesRequireWarning(field, event)) return
                  updateField(field, { value_type: event.target.value });
                }}
                className='form-control'
              >
                <option value='text'>Text</option>
                <option value='long_text'>Long Text</option>
                <option value='date'>Date</option>
                <option value='link'>Link</option>
                <option value='select'>Select</option>
                <option value='dynamic'>Dynamic</option>
              </select>
            )}
          </div>

          {!field.system_owned && field.value_type == 'link' && 
            <div className='form-group'>
              <label>Anchor Text</label>
              <input
                className='form-control'
                value={field.anchor_text}
                onChange={(event) => {
                  updateField(field, { anchor_text: event.target.value });
                }}
              />
            </div>
          }
        </div>
        <div className='col-md-4'>
          {props.recordType != "sponsorship" && (
            <React.Fragment>
              <div className='form-group'>
                <ToggleSwitch
                  small
                  id={getFieldId(field) + "_visible"}
                  checked={field.visible}
                  onChange={(checked) => {
                    updateField(field, { visible: checked });
                  }}
                />
                <label htmlFor={getFieldId(field) + "_visible"}>
                  Show on profile
                </label>
              </div>
              <div className='form-group'>
                <ToggleSwitch
                  small
                  id={getFieldId(field) + "_editable"}
                  checked={field.editable}
                  onChange={(checked) => {
                    updateField(field, { editable: checked });
                  }}
                />
                <label htmlFor={getFieldId(field) + "_editable"}>
                  Editable by {translations[props.recordType].toLowerCase()}
                </label>
              </div>
              <div className='form-group'>
                <ToggleSwitch
                  small
                  id={getFieldId(field) + "_notification"}
                  checked={field.send_notifications}
                  onChange={(checked) => {
                    updateField(field, { send_notifications: checked });
                  }}
                />
                <label htmlFor={getFieldId(field) + "_notification"}>
                  {props.recordType.toLowerCase() == "donor" && <>Inform {naming.beneficiary} of changes</>}
                  {props.recordType.toLowerCase() == "beneficiary" && <>Inform {naming.donor} of changes</>}
                  {props.recordType.toLowerCase() == "project" && <>Inform contacts of changes</>}
                </label>
              </div>
            </React.Fragment>
          )}
        </div>
        {!field.system_owned && (
          <div className='col-md-1'>
            <button
              onClick={() => {
                handleSetDeleting(field.id ? field.id : field.new);
              }}
              type='button'
              className='btn btn-icon btn-outline-danger'
            >
              <i className='fa fa-trash'></i>
            </button>
          </div>
        )}
      </React.Fragment>
    )}
    </div>

    {field.value_type == 'select' &&
      <div className="row form-group">
        <div className="col-md-12">
          <div 
            className="btn btn-primary btn-sm" 
            onClick={() => handleOpenModal(field.id ? field.id : field.new)}
          >
            Manage Select Options
          </div>
        </div>
      </div>
    }
    <hr />

    {rightFieldForAction(openModal, field) &&
      <SelectOptionsModal 
        options={field.select_options} 
        handleClose={(field, options) => closeOptionsModal(field, options)} 
        field={field}
      />
    }
  </div>
))

const SortableContainer = sortableContainer(({ children }) => {
  return <div>{children}</div>;
});

export default function CustomFields(props) {
  const [customFields, setCustomFields] = useState(props.customFields);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
	const [openModal, setOpenModal] = useState(false)
  const [pendingUpdate, setPendingUpdate] = useState(null)
  const [warningModal, setWarningModal] = useState(null)
  const naming = props.naming 
  const fieldType = ['text', 'long_text', 'date', 'link', 'select', 'dynamic']

  const handleSubmit = () => {
    if (invalidData()) return

    setSaving(true)
    HTTP.patch(`/custom_fields/${props.recordType}`, {
      record_type: props.recordType,
      custom_fields: customFields 
    }).then(response => {
      Alert.show("notice", "Settings saved!");
      setCustomFields(response.data);
      
      setSaving(false);
    }).catch(e => {
      Alert.show("error", "Could not save. Try again or refresh.");
      setSaving(false);
    })
  }

  const invalidData = () => {
    let reservedFields = props.reserved_names || []

    if (reservedFields.some(reservedField => customFields.some(field => field.name.toLowerCase() === reservedField.toLowerCase()))) {
      Alert.show("error", "Reserved field name. Please choose a different name.");
      return true;
    }

    if (customFields.some((field, index) => customFields.findIndex(f => f.name.toLowerCase() === field.name.toLowerCase()) !== index)) {
      Alert.show("error", "Duplicate field name. Please choose a different name.");
      return true;
    }

    return false
  }
  
  const handleAddField = (type) => {
    setCustomFields([...customFields, {
      "new": `new-${customFields.length}`,
      "name":"New Field",
      "value_type":type,
      "record_type":"donor",
      "editable":true,
      "visible":true, 
      "anchor_text": '',
      "select_options": []
    }]);
  }

  const getFieldId = (field) => {
    if (field.id) return field.id
    return field.new
  }

  const handleDelete = (field) => {
    removeField(field)
    if (!field.id) {
      removeUnsavedField(field)
      return
    }

    HTTP.delete(`/custom_fields/${props.recordType}/${field.id}`)
    .then(response => {
      Alert.show("notice", "Field removed!");
      setDeleting(null)
      window.location.reload()
    }).catch(e => {
      Alert.show("error", "Could not remove. Try again or refresh.");
      setSaving(false);
    })
  }

  const removeField = (field) => {
    let array = [...customFields]; // make a separate copy of the array
    _.remove(array, { _id: field._id })
    setCustomFields(array);
  }

  const removeUnsavedField = (field) => {
    let array = [...customFields]; // make a separate copy of the array
    _.remove(array, { new: field.new })
    setCustomFields(array);
    setDeleting(null)
  }

  const updateField = (field, update) => {
    setCustomFields([...customFields].map(object => {
      let objectId = object.id ? object.id : object.new 
      let fieldId = field.id ? field.id : field.new
      if(objectId === fieldId) {
        return {
          ...object,
          ...update
        }
      }
      else return object;
    }))
  }

	const closeOptionsModal = (field, options) => {
		updateField(field, { select_options: options})
		setOpenModal(false)
	}

  const rightFieldForAction = (deleting, field) => {
    let id = field.id ? field.id : field.new 
    if (deleting == id)  return true 
    if (deleting != id) return false
  }

  const onSortEnd = ({oldIndex, newIndex}) => {
    setCustomFields(arrayMove(customFields, oldIndex, newIndex))
    updateOrder(arrayMove(customFields, oldIndex, newIndex))
  }

  const updateOrder = async (fields) => {
    setSaving(true)
    try {
      let response = await orderRoute(fields) 
      Alert.show('notice', 'Order saved')
      setCustomFields(response.data)
      setSaving(false)
    } catch {
      Alert.show('error', 'Could not update order')
      setSaving(false)
    }
  }

  const orderRoute = (fields) => {
    return HTTP.patch(`/custom_fields/${props.recordType}/update_order`, {
      record_type: props.recordType,
      custom_fields: fields 
    })
  }

  const handleSetDeleting = (value) => {
    setDeleting(value)
  }

  const handleOpenModal = (value) => {
    setOpenModal(value)
  }

  const changesRequireWarning = (field, event) => {
    if (!field.value_type) return false

    let oldType = 'string'
    let newType = 'string'

    if (field.value_type == 'date') oldType = 'date'
    if (event.target.value == 'date') newType = 'date'
    if (field.value_type == 'select') oldType = 'select'
    if (event.target.value == 'select') newType = 'select'
    if (field.value_type == 'dynamic') oldType = 'dynamic'
    if (event.target.value == 'dynamic') newType = 'dynamic'

    if (oldType == newType) {
      return false
    }

    if (oldType !== newType) {
      setPendingUpdate({
        field: field, 
        object: { value_type: event.target.value}, 
        oldFormat: oldType, 
        newFormat: newType
      })
      setWarningModal(field.id ? field.id : field.new)
      return true
    }
  }

  const allowFieldTypeChange = () => {
    updateField(pendingUpdate.field, pendingUpdate.object)
    setPendingUpdate(null)
    setWarningModal(false)
  }

  const cancelFieldTypeChange = () => {
    setPendingUpdate(null)
    setWarningModal(false)
  }

  const capitalizeText = (str) => {
      return str
          .replaceAll('_', ' ')
          .replace(/\b\w/g, char => char.toUpperCase());
  }

  return (
    <React.Fragment>
      <div className="card">
        <div className="card-header flex-header">
          <div>
            <h4 className="card-title"
              style={{textTransform: 'capitalize'}}
            >
              {translations[props.recordType]} Fields
            </h4>
          </div>
          <div>
            <div className="dropdown show" style={{display: 'unset'}}>
              <button name="button" className="btn btn-secondary mr-2" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                + Add Field
              </button>
              <div class="dropdown-menu" aria-labelledby="dropdownMenuLink">
                  {fieldType && fieldType.map((type, index) => (
                      <a key={index} class="dropdown-item" href="#" onClick={() => handleAddField(type)}>{capitalizeText(type)}</a>
                  ))}
              </div>
            </div>
            <button onClick={handleSubmit} name="button" className="btn btn-primary">
              {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
              Save
            </button>
          </div>
        </div>


        <div className="card-body">
          <SortableContainer onSortEnd={onSortEnd} useDragHandle>
            {customFields && customFields.map((field, index) => (
              <SortableItem 
                key={field.id ? field.id : field.new} 
                index={index} 
                field={field} 
                rightFieldForAction={(action, field) => rightFieldForAction(action, field)}
                deleting={deleting}
                handleSetDeleting={handleSetDeleting}
                props={props}
                openModal={openModal}
                updateField={(field, value) => updateField(field, value)}
                handleOpenModal={(value) => handleOpenModal(value)}
                closeOptionsModal={(field, options) => closeOptionsModal(field, options)}
                handleDelete={(field) => handleDelete(field)}
                changesRequireWarning={(field, value) => changesRequireWarning(field, value)}
                getFieldId={(field) => getFieldId(field)}
                naming={naming}
              />
            ))}
          </SortableContainer>
        </div>

      {warningModal &&
       <TextFormatWarning 
          oldType={pendingUpdate.oldFormat} 
          newType={pendingUpdate.newFormat} 
          proceed={allowFieldTypeChange}
          cancel={cancelFieldTypeChange}
        />
      }
      </div>
    </React.Fragment>
  );
}

CustomFields.propTypes = {
  recordType: PropTypes.string,
  customFields: PropTypes.array
};
